import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { AlertCircle, Bell, BellOff, ChevronLeft, ChevronRight, ExternalLink, RefreshCw } from "lucide-react";
import { AppLink } from "@/components/AppLink";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { alarm as alarmApi, type LensStructureAlarmResponse } from "@/endpoints/dryp/alarm";
import { CUSTOM_FILTERS } from "@/config/list";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/utils/number";
import { formatAlarmCondition } from "@/pages/Dashboard/Alarms/types";
import { Box, BoxHeader, BoxContent } from "./Box";

type StructureAlarmsProps = {
	structureId: Dryp.StructureId;
	isActive: boolean;
};

const PAGE_SIZE = 5;

const PulseDot = () => (
	<span className="relative flex h-2 w-2 shrink-0">
		<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
		<span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
	</span>
);

const IdleDot = () => <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500/80" />;

const PendingDot = () => <span className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground/40" />;

type AlarmStatus = "triggered" | "idle" | "pending";

const getStatus = (alarm: LensStructureAlarmResponse): AlarmStatus => {
	if (!alarm.alarmState) return "pending";
	return alarm.alarmState.isTriggered ? "triggered" : "idle";
};

const AlarmRow = ({ alarm }: { alarm: LensStructureAlarmResponse }) => {
	const status = getStatus(alarm);
	const triggerValue = alarm.alarmState?.triggerValue;
	return (
		<AppLink
			to="Dashboard.Alarms"
			params={{}}
			query={{ filter: [{ id: CUSTOM_FILTERS.search, value: String(alarm.id) }] }}
			className={cn(
				"group relative block overflow-hidden rounded-md px-3 py-2 pl-3.5 transition-colors",
				status === "triggered" && "bg-red-50 hover:bg-red-100/70 dark:bg-red-950/30 dark:hover:bg-red-950/50",
				status === "idle" &&
					"bg-emerald-50/60 hover:bg-emerald-100/60 dark:bg-emerald-950/20 dark:hover:bg-emerald-950/40",
				status === "pending" && "bg-muted/40 hover:bg-muted/60",
			)}
		>
			<span
				aria-hidden="true"
				className={cn(
					"absolute inset-y-0 left-0 w-1",
					status === "triggered" && "bg-red-500",
					status === "idle" && "bg-emerald-500/60",
					status === "pending" && "bg-muted-foreground/30",
				)}
			/>

			<div className="flex min-w-0 items-start gap-2">
				<span className="mt-1 flex h-2 w-2 items-center justify-center">
					{status === "triggered" && <PulseDot />}
					{status === "idle" && <IdleDot />}
					{status === "pending" && <PendingDot />}
				</span>
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<p className="truncate text-xs font-semibold group-hover:text-primary">{alarm.name}</p>
						{triggerValue != null && (
							<span className="ml-auto shrink-0 rounded bg-red-500/10 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-red-600 dark:text-red-400">
								{formatNumber(triggerValue, 2)} {alarm.parameter.unit.symbol}
							</span>
						)}
					</div>
					<p className="mt-0.5 truncate text-[11px] text-muted-foreground">{formatAlarmCondition(alarm)}</p>
				</div>
			</div>
		</AppLink>
	);
};

export const StructureAlarms = observer(({ structureId, isActive }: StructureAlarmsProps) => {
	const alarmsStructureApi = useAsyncStateStore(alarmApi.getForStructure);
	const [page, setPage] = useState(0);

	useEffect(() => {
		if (!isActive) return;
		alarmsStructureApi.fetch(structureId, { pageNumber: page, pageSize: PAGE_SIZE });
	}, [structureId, page, isActive]);

	const alarms = alarmsStructureApi.data?.items ?? [];
	const totalItems = alarmsStructureApi.data?.query?.pagination.totalItems ?? 0;
	const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
	const triggeredCount = alarmsStructureApi.data?.itemsMeta?.triggeredCount ?? 0;
	const showSkeleton = alarmsStructureApi.loading && !alarmsStructureApi.loaded;
	const error = alarmsStructureApi.error as ApiError | null;

	return (
		<Box>
			<BoxHeader
				icon={<Bell className="w-4 h-4" />}
				title="Alarms"
				action={
					<AppLink
						to="Dashboard.Alarms"
						params={{}}
						className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
					>
						See all alarms
						<ExternalLink className="w-3 h-3" />
					</AppLink>
				}
			/>
			<BoxContent>
				{!isActive ? (
					<div className="flex flex-col items-center gap-1.5 rounded-md bg-muted/40 px-3 py-5 text-center">
						<BellOff className="h-5 w-5 text-muted-foreground" />
						<p className="text-xs font-medium">Structure deactivated</p>
						<p className="text-[11px] text-muted-foreground">
							Alarms aren&apos;t monitored while it&apos;s deactivated.
						</p>
					</div>
				) : showSkeleton ? (
					<div className="space-y-2">
						<Skeleton className="h-10 w-full" />
						<Skeleton className="h-10 w-full" />
						<Skeleton className="h-10 w-full" />
					</div>
				) : error ? (
					<div className="flex items-center justify-between gap-2 rounded-md border border-destructive/20 bg-destructive/10 p-3 text-xs text-destructive">
						<span className="flex items-center gap-2">
							<AlertCircle className="h-3.5 w-3.5 shrink-0" />
							{error.message || "Couldn't load alarms."}
						</span>
						<button
							type="button"
							onClick={() => alarmsStructureApi.refetch()}
							className="inline-flex items-center gap-1 text-[11px] font-medium underline-offset-2 hover:underline"
						>
							<RefreshCw className="h-3 w-3" />
							Retry
						</button>
					</div>
				) : alarms.length === 0 ? (
					<p className="text-xs text-muted-foreground">No alarms configured for this structure.</p>
				) : (
					<div
						className={alarmsStructureApi.loading ? "transition-opacity opacity-50" : "transition-opacity"}
					>
						{triggeredCount > 0 && (
							<div className="mb-2 flex items-center gap-2 rounded-md bg-destructive/10 px-2 py-1.5 text-xs font-medium text-destructive">
								<Bell className="w-3.5 h-3.5" />
								{triggeredCount} alarm{triggeredCount === 1 ? "" : "s"} firing
							</div>
						)}
						<div className="space-y-1">
							{alarms.map(alarm => (
								<AlarmRow key={alarm.id} alarm={alarm} />
							))}
						</div>
						{totalPages > 1 && (
							<div className="mt-3 flex items-center justify-between">
								<Button
									variant="outline"
									size="sm"
									className="h-7 px-2"
									onClick={() => setPage(prev => Math.max(0, prev - 1))}
									disabled={page === 0 || alarmsStructureApi.loading}
								>
									<ChevronLeft className="h-3.5 w-3.5" />
								</Button>
								<span className="text-[11px] text-muted-foreground tabular-nums">
									{page + 1} / {totalPages} · {totalItems} total
								</span>
								<Button
									variant="outline"
									size="sm"
									className="h-7 px-2"
									onClick={() => setPage(prev => Math.min(totalPages - 1, prev + 1))}
									disabled={page >= totalPages - 1 || alarmsStructureApi.loading}
								>
									<ChevronRight className="h-3.5 w-3.5" />
								</Button>
							</div>
						)}
					</div>
				)}
			</BoxContent>
		</Box>
	);
});
